import { Shell } from "@/components/layout/Shell";
import { QuizFlow } from "@/components/quiz/QuizFlow";
import { generateDecisionRounds, analyzeDecision, Round } from "@/data/quiz";
import { useSeo } from "@/hooks/use-seo";
import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "wouter";
import { ShareButtons } from "@/components/social/ShareButtons";

export default function QuizDecision() {
  useSeo({
    title: "Decision Color Quiz | Mychromamind",
    description:
      "Weighing a choice? A 5-step color quiz that maps out the conflict, the risk, and your true desire using color psychology.",
    path: "/quiz/decision",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Decision Color Quiz",
      applicationCategory: "LifestyleApplication",
      operatingSystem: "Any (Web)",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      description:
        "A 5-step color quiz that maps out the conflict, the risk, and your true desire behind a decision using color psychology.",
      url: "https://mychromamind.com/quiz/decision",
    },
  });

  const matrixCta = (
    <p className="text-sm text-muted-foreground text-center mt-2">
      Want to name the actual dilemma?{" "}
      <Link href="/decision-matrix" className="text-primary hover:underline underline-offset-4">
        Try the Color Decision Matrix →
      </Link>
    </p>
  );

  const [rounds, setRounds] = useState<Round[]>(() => generateDecisionRounds());
  const [isComplete, setIsComplete] = useState(false);
  const [result, setResult] = useState<ReturnType<typeof analyzeDecision> | null>(null);

  const handleComplete = (choices: string[]) => {
    const analysis = analyzeDecision(rounds, choices);
    setResult(analysis);
    setIsComplete(true);
  };

  const handleRetake = () => {
    setRounds(generateDecisionRounds());
    setIsComplete(false);
    setResult(null);
  };

  return (
    <Shell>
      {!isComplete ? (
        <>
          <h1 className="sr-only">Decision Color Quiz</h1>
          <QuizFlow rounds={rounds} onComplete={handleComplete} />
          <div className="pb-10">{matrixCta}</div>
        </>
      ) : (
        <div className="flex-1 flex flex-col items-center px-6 py-12 md:py-24 max-w-3xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="w-full text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-serif mb-4">Your Instincts</h1>
            <p className="text-muted-foreground text-lg">A reflection of your underlying feelings regarding this decision.</p>
          </motion.div>

          <div className="w-full space-y-8">
            {result?.insights.map((insight, i) => (
              <motion.div
                key={insight.step}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + i * 0.3, duration: 0.8 }}
                className="flex flex-col sm:flex-row gap-6 items-start bg-card p-6 rounded-2xl border border-border shadow-sm"
              >
                <div 
                  className="w-16 h-16 rounded-full flex-shrink-0 shadow-inner"
                  style={{ backgroundColor: insight.color.hex }}
                />
                <div className="text-left">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-2">{insight.step}</h3>
                  <p className="text-foreground text-lg leading-relaxed">{insight.text}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3, duration: 1 }}
            className="mt-16 flex flex-col sm:flex-row gap-4 justify-center items-center w-full"
          >
            <button 
              onClick={handleRetake}
              className="px-6 py-3 rounded-full border border-border hover:bg-secondary/5 transition-colors font-medium text-sm"
            >
              Analyze Another Decision
            </button>
            <Link href="/learn" className="px-6 py-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium text-sm">
              Read Color Meanings
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3.3, duration: 1 }}
            className="mt-10 w-full flex flex-col items-center"
          >
            <ShareButtons
              label="Share this reading"
              text="I just mapped out a decision with the Mychromamind color quiz."
            />
            <div className="mt-6">{matrixCta}</div>
            <p className="mt-8 text-xs text-muted-foreground max-w-md mx-auto text-center">
              For fun and reflection only — not a substitute for professional advice.
            </p>
          </motion.div>
        </div>
      )}
    </Shell>
  );
}
